/**
 * Channels — Unified Distribute view across Social / Email / Blog / Paid.
 *
 * Wired to `/api/marketing/channels` (May 2026) for live connection state
 * + queued/sent counters. Per-channel connect/disconnect calls trigger an
 * optimistic re-fetch so reps see their changes instantly.
 *
 * The send-queue table below the tiles still has no first-party publisher
 * to read from — when a Marketing Scheduler lands, drop its events into
 * the `queueRows` state via the same `/api/marketing/channels/queue`
 * endpoint shape.
 */
import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import MarketingShell from "@/components/MarketingShell";
import {
  Linkedin, Instagram, Twitter, Facebook, Youtube, Mail, FileText,
  Megaphone, Globe, CheckCircle2, Clock, Loader2,
} from "lucide-react";

const CHANNEL_VISUAL = {
  linkedin:  { icon: Linkedin,  color: "#0077B5" },
  instagram: { icon: Instagram, color: "#E4405F" },
  x:         { icon: Twitter,   color: "#0f1419" },
  facebook:  { icon: Facebook,  color: "#1877F2" },
  youtube:   { icon: Youtube,   color: "#FF0000" },
  email:     { icon: Mail,      color: "#2E63B8" },
  blog:      { icon: FileText,  color: "#1B365D" },
  paid:      { icon: Megaphone, color: "#7A33FF" },
  gbp:       { icon: Globe,     color: "#34A853" },
};

export default function Channels() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState(searchParams.get("channel") || "all");
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const queueRows = []; // Reserved for the Marketing Scheduler — empty for now.

  const loadChannels = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/marketing/channels`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setChannels(res.data?.items || []);
    } catch {
      setChannels([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadChannels(); }, [loadChannels]);

  useEffect(() => {
    const urlCh = searchParams.get("channel");
    setFilter(urlCh || "all");
  }, [searchParams]);

  const setActiveFilter = (next) => {
    setFilter(next);
    const sp = new URLSearchParams(searchParams);
    if (next === "all") sp.delete("channel"); else sp.set("channel", next);
    setSearchParams(sp, { replace: true });
  };

  const toggleConnect = async (channel) => {
    setBusyId(channel.id);
    try {
      const verb = channel.connected ? "disconnect" : "connect";
      await axios.put(
        `${API}/marketing/channels/${channel.id}/${verb}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`${channel.label} ${channel.connected ? "disconnected" : "marked as connected"}`);
      loadChannels();
    } catch {
      toast.error("Could not update channel — try again");
    } finally {
      setBusyId(null);
    }
  };

  const filtered = filter === "all" ? queueRows : queueRows.filter((q) => q.channel === filter);
  const activeLabel = channels.find((c) => c.id === filter)?.label;

  if (loading) {
    return (
      <MarketingShell title="Channels" subtitle="Loading…">
        <div style={{ display: "flex", justifyContent: "center", padding: 80 }} data-testid="channels-loading">
          <Loader2 style={{ width: 24, height: 24, animation: "spin 1s linear infinite", color: "var(--blue)" }} />
        </div>
      </MarketingShell>
    );
  }

  return (
    <MarketingShell title="Channels" subtitle="Unified send queue across every distribution channel">
      <div data-testid="channels-page">
        {/* Channel tiles */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 22 }}>
          {channels.map((c) => {
            const v = CHANNEL_VISUAL[c.id] || { icon: Globe, color: "#666" };
            const Icon = v.icon;
            const isBusy = busyId === c.id;
            return (
              <div
                key={c.id}
                className="mkt-card is-interactive"
                style={{ padding: "14px 16px", border: filter === c.id ? "1px solid var(--blue)" : "0.5px solid var(--border)", cursor: "pointer" }}
                data-testid={`channel-tile-${c.id}`}
                onClick={() => setActiveFilter(c.id === filter ? "all" : c.id)}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 8, background: `${v.color}15`, color: v.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon style={{ width: 16, height: 16 }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 580, color: "var(--navy)" }}>{c.label}</div>
                    <div style={{ fontSize: 10.5, color: "var(--text-tertiary)", marginTop: 2 }}>
                      {c.connected
                        ? `${c.queued_count || 0} queued · ${c.sent_count || 0} sent${c.account_name ? ` · ${c.account_name}` : ""}`
                        : "Not connected"}
                    </div>
                  </div>
                  <span className={`tag ${c.connected ? "tag-green" : "tag-gray"}`}>{c.connected ? "Live" : "Off"}</span>
                </div>
                <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
                  <button
                    className="btn"
                    style={{ height: 26, padding: "0 10px", fontSize: 11 }}
                    onClick={(e) => { e.stopPropagation(); toggleConnect(c); }}
                    data-testid={`channel-${c.id}-toggle`}
                    disabled={isBusy}
                  >
                    {isBusy
                      ? <Loader2 style={{ width: 11, height: 11, animation: "spin 1s linear infinite" }} />
                      : (c.connected ? "Disconnect" : "Mark as connected")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Queue */}
        <div className="mkt-table-wrap">
          <div style={{ padding: "14px 18px", borderBottom: "0.5px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h3 className="card-title">Send queue {filter !== "all" && <span style={{ fontWeight: 400, color: "var(--text-tertiary)" }}>· {activeLabel}</span>}</h3>
            {filter !== "all" && <button onClick={() => setActiveFilter("all")} className="btn" style={{ height: 26, padding: "0 9px", fontSize: 11 }}>Clear filter</button>}
          </div>
          <table className="mkt-table">
            <thead>
              <tr>
                {["Channel", "Title", "Scheduled", "Status"].map(h => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.map((q) => {
                const v = CHANNEL_VISUAL[q.channel] || { icon: Globe, color: "#666" };
                const Icon = v.icon;
                const ch = channels.find((c) => c.id === q.channel);
                return (
                  <tr key={q.id} data-testid={`queue-row-${q.id}`}>
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                        <Icon style={{ width: 13, height: 13, color: v.color }} /> {ch?.label}
                      </span>
                    </td>
                    <td style={{ fontSize: 13, fontWeight: 500 }}>{q.title}</td>
                    <td style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Clock style={{ width: 11, height: 11 }} /> {q.scheduled}
                      </span>
                    </td>
                    <td>
                      <span className={`tag ${q.status === "scheduled" ? "tag-purple" : "tag-amber"}`}>
                        {q.status === "scheduled" ? <><CheckCircle2 style={{ width: 10, height: 10, display: "inline" }} /> Scheduled</> : "Draft"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div data-testid="queue-empty" style={{ padding: 40, textAlign: "center", color: "var(--text-tertiary)", fontSize: 12.5 }}>
              Nothing scheduled. Connect a channel and schedule content from the Calendar or Content Studio.
            </div>
          )}
        </div>
      </div>
    </MarketingShell>
  );
}
